import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CustomRouterNavigatedAction } from '../../../core/custom-route-serializer';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import * as statisticsAction from '../statistics/statistics.actions';
import { SETTINGS_ROUTES } from '../../settings/settings.routes';
import { of } from 'rxjs';
import { StatisticsService } from '../../services/statistics/statistics.service';
import { ToastrService } from 'ngx-toastr';

@Injectable()
export class StatisticsEffects {
  studiesRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CustomRouterNavigatedAction>(ROUTER_NAVIGATED),
      filter((route: CustomRouterNavigatedAction) => {
        return route.payload.routerState.path === SETTINGS_ROUTES.STATUS;
      }),
      switchMap(() => [
        statisticsAction.getStatistics(),
        statisticsAction.getStatusApi(),
        statisticsAction.getStatusDashboard(),
        statisticsAction.getStatusAndroid(),
        statisticsAction.getStatusApple(),
      ])
    )
  );

  getStatistics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(statisticsAction.getStatistics),
      switchMap(() => {
        return this.statisticsService.getStatistics().pipe(
          map((statistics) => statisticsAction.getStatisticsSuccess({ statistics })),
          catchError((err) => of(statisticsAction.getStatisticsError()))
        );
      })
    )
  );

  getStatisticsError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(statisticsAction.getStatisticsError),
        tap(() => this.toastrService.error('Error in fetching Answer Statistics'))
      ),
    { dispatch: false }
  );

  getStatusApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(statisticsAction.getStatusApi),
      switchMap(() => {
        return this.statisticsService.getStatusApi().pipe(
          map(() => statisticsAction.getStatusApiSuccess()),
          catchError(() => of(statisticsAction.getStatusApiError()))
        );
      })
    )
  );

  getStatusApiError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(statisticsAction.getStatusApiError),
        tap(() => this.toastrService.error('Unable to fetch Server status'))
      ),
    { dispatch: false }
  );

  getStatusDashboard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(statisticsAction.getStatusDashboard),
      switchMap(() => {
        return this.statisticsService.getStatusDashboard().pipe(
          map(() => statisticsAction.getStatusDashboardSuccess()),
          catchError(() => of(statisticsAction.getStatusDashboardError()))
        );
      })
    )
  );

  getStatusDashboardError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(statisticsAction.getStatusDashboardError),
        tap(() => this.toastrService.error('Unable to fetch Dashboard status'))
      ),
    { dispatch: false }
  );

  getStatusAndroid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(statisticsAction.getStatusAndroid),
      switchMap(() => {
        return this.statisticsService.getStatusAndroid().pipe(
          map(() => statisticsAction.getStatusAndroidSuccess()),
          catchError(() => of(statisticsAction.getStatusAndroidError()))
        );
      })
    )
  );

  getStatusAndroidError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(statisticsAction.getStatusAndroidError),
        tap(() => this.toastrService.error('Unable to fetch Android status'))
      ),
    { dispatch: false }
  );

  getStatusApple$ = createEffect(() =>
    this.actions$.pipe(
      ofType(statisticsAction.getStatusApple),
      switchMap(() => {
        return this.statisticsService.getStatusApple().pipe(
          map(() => statisticsAction.getStatusAppleSuccess()),
          catchError(() => of(statisticsAction.getStatusAppleError()))
        );
      })
    )
  );

  getStatusAppleError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(statisticsAction.getStatusAppleError),
        tap(() => this.toastrService.error('Unable to fetch Apple status'))
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private statisticsService: StatisticsService,
    private toastrService: ToastrService
  ) {}
}
